/**
 * "An investor already scored your deck" — claim it.
 *
 * WHY THIS EXISTS
 *   A founder whose deck was submitted to an investor gets a claim link by email.
 *   If they lose that email they had no way back in: claim tokens are stored as a
 *   SHA-256 hash and the raw value is never persisted, so it cannot be re-sent.
 *   The server-side lookup to find these has existed for months — unreferenced,
 *   because it could not complete a claim without this surface and without the
 *   `submissionId` claim path.
 *
 * WHAT IT TRUSTS
 *   Nothing. The lookup is scoped server-side to the caller's own verified email,
 *   and claiming re-derives that match from the submission row rather than
 *   trusting this screen to have asked the right question.
 */
import React from 'react';
import { View, Text } from 'react-native';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import {
  lookupUnclaimedSubmissions,
  claimSubmissionById,
  type UnclaimedSubmissionMatch,
} from '@/lib/supabase/edge-functions';

export const unclaimedSubmissionKeys = {
  mine: () => ['unclaimed-submissions', 'mine'] as const,
};

export function UnclaimedSubmissionsCard() {
  const colors = useThemeColors();
  const queryClient = useQueryClient();
  const { error: showError } = useAlert();

  const { data: matches = [] } = useQuery({
    queryKey: unclaimedSubmissionKeys.mine(),
    queryFn: async (): Promise<UnclaimedSubmissionMatch[]> => {
      const res = await lookupUnclaimedSubmissions();
      // A failed lookup is not an empty lookup. Returning [] on error would hide
      // a claimable submission behind a transient network blip, with no way for
      // the founder to know it existed.
      if (res.error) throw res.error;
      return res.data?.matches ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const claim = useMutation({
    mutationFn: async (submissionId: string) => {
      const res = await claimSubmissionById(submissionId);
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // The claim changes what the dashboard shows, not just this card.
      queryClient.invalidateQueries({ queryKey: unclaimedSubmissionKeys.mine() });
      queryClient.invalidateQueries({ queryKey: ['claimed-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['score'] });
    },
    onError: (err) => {
      const { message, isCancelled } = handleError(err, {
        context: 'UnclaimedSubmissionsCard.claim',
      });
      if (!isCancelled) showError('Could not claim', message);
    },
  });

  if (matches.length === 0) return null;

  return (
    <Animated.View entering={FadeInUp.duration(400)} className="px-5 mb-4">
      {matches.map((m) => (
        <View
          key={m.submissionId}
          className="rounded-2xl p-4 mb-3 overflow-hidden"
          style={{
            backgroundColor: colors.primary[50],
            borderWidth: 1,
            borderColor: colors.primary[200],
          }}
        >
          <View className="flex-row items-center gap-3 mb-3">
            <View
              className="w-10 h-10 rounded-xl items-center justify-center shrink-0"
              style={{ backgroundColor: colors.primary[600] }}
            >
              <FileText size={20} color="white" />
            </View>
            <View className="flex-1">
              <Text
                style={{ color: colors.text.primary }}
                className="font-semibold text-sm"
                numberOfLines={1}
              >
                {m.companyName}
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-xs"
                numberOfLines={2}
              >
                {m.investorName} scored this pitch deck
                {typeof m.score === 'number' ? ` · ${m.score}/100` : ''}
              </Text>
            </View>
          </View>

          {/* The View owns the height; the Pressable fills it. A callback style
              with dimensions does not compute reliably in RN 0.81+. */}
          <View
            style={{
              height: 44,
              borderRadius: 10,
              backgroundColor: colors.primary[600],
              overflow: 'hidden',
            }}
          >
            <AnimatedPressable
              className="flex-1 items-center justify-center"
              disabled={claim.isPending}
              onPress={() => claim.mutate(m.submissionId)}
              accessibilityLabel={`Claim the scored submission for ${m.companyName}`}
            >
              <Text style={{ color: colors.text.inverse }} className="text-sm font-semibold">
                {claim.isPending ? 'Claiming…' : 'Claim this report'}
              </Text>
            </AnimatedPressable>
          </View>
        </View>
      ))}
    </Animated.View>
  );
}
